import React, { useCallback } from 'react'
import { Offcanvas } from 'react-bootstrap'
import { Button } from '../../components/Button/Button'
import { IntegrationIcon } from '../../components/IntegrationIcon'
import { ContactDropdown } from '../MeetingSummary/CRM/ContactDropdown'
import { getIntegrationDisplayName } from '../../common/integrations'
import { User } from '../../models/User'
import { IntegrationObjectRecordPicker } from './IntegrationObjectRecordPicker'
import { IntegrationContactOption, UseUpdateIntegrationObject } from './useUpdateIntegrationObject'
import '../../apps/professional-app/fa/MeetingSummary/Sidebar/SummarySidebar.scss'
import './UpdateIntegrationObjectWizard.scss'

interface UpdateIntegrationObjectWizardProps {
  updateIntegrationObject: UseUpdateIntegrationObject
  onHide: () => void
}

const userDisplayName = (user: Pick<User, 'firstName' | 'lastName' | 'name' | 'email'>): string =>
  [user.firstName, user.lastName].filter(Boolean).join(' ') || user.name || user.email || 'Unknown contact'

export const UpdateIntegrationObjectWizard: React.FC<UpdateIntegrationObjectWizardProps> = ({ updateIntegrationObject, onHide }) => {
  const { object, phase, selectContact } = updateIntegrationObject
  const show = phase === 'record'

  const toContactOption = useCallback(
    (user: User): IntegrationContactOption | null => {
      const externalId = user.integrations?.find((i) => i.serviceId === object?.serviceId)?.externalId
      return externalId ? { id: user.id, name: userDisplayName(user), externalId } : null
    },
    [object?.serviceId],
  )

  const handleContactSelected = useCallback((user: User) => selectContact(toContactOption(user)), [selectContact, toContactOption])

  if (!object) {
    return null
  }

  return (
    <Offcanvas
      show={show}
      placement='end'
      className='summary-sidebar-container update-integration-object-wizard'
      onHide={onHide}
      scroll
      backdrop={false}
      animation={false}
    >
      <div className='h-100 py-3 pe-4 ps-3 d-flex flex-column mb-9'>
        <div className='d-flex p-4 align-items-center justify-content-between summary-sidebar-header'>
          <div className='d-flex align-items-center gap-2'>
            <IntegrationIcon serviceId={object.serviceId} fill='white' className='update-integration-object-wizard__icon' />
            <div className='paragraph-large bold text-white'>Update CRM object</div>
          </div>
          <Button variant='link' onClick={onHide} aria-label='Close'>
            <i className='bi bi-x-lg text-white' aria-hidden='true' />
          </Button>
        </div>

        <div className='flex-grow-1 overflow-auto p-4 summary-sidebar-content-container'>
          <div className='mb-4'>
            <div className='paragraph-large-bold mb-1'>Push meeting updates to your CRM</div>
            <div className='paragraph-medium zocks-text-gray-600'>
              Zocks can take the new information that came up during this meeting and update it directly in your connected CRM.
            </div>
          </div>

          {object.contactRequired ? (
            <div className='mb-4'>
              <div className='mb-2'>
                <div className='paragraph-large-bold mb-1'>Select contact</div>
                <div className='paragraph-medium zocks-text-gray-600'>{object.displayName} records need a contact so Zocks can find the right one.</div>
              </div>
              <div>
                <div className='paragraph-small-bold zocks-text-gray-600 mb-1'>Contact</div>
                <ContactDropdown serviceId={object.serviceId} prefetchContacts showLabel={false} onContactSelected={handleContactSelected} />
              </div>
            </div>
          ) : null}

          <div className='mb-3'>
            <div className='paragraph-large-bold mb-1'>Select the {object.displayName}</div>
            <div className='paragraph-medium zocks-text-gray-600'>
              Pick the specific {object.displayName} record from {getIntegrationDisplayName(object.serviceId)} you'd like to update.
              {object.contactRequired && updateIntegrationObject.contactName ? ` Showing records related to ${updateIntegrationObject.contactName}.` : ''}
            </div>
          </div>
          {!object.contactRequired ? (
            <div className='update-integration-object-fallback paragraph-small mb-3'>
              <i className='bi bi-info-circle me-1' />
              {object.displayName} records aren't linked to a contact, so we'll go straight to picking a record.
            </div>
          ) : null}
          <IntegrationObjectRecordPicker
            object={object}
            contactExternalId={updateIntegrationObject.contactExternalId}
            contactName={updateIntegrationObject.contactName}
            targetRecordId={updateIntegrationObject.targetRecordId}
            onSelectRecord={updateIntegrationObject.selectRecord}
            onChooseCreateNew={updateIntegrationObject.chooseCreateNew}
            onReviewUpdates={updateIntegrationObject.reviewUpdates}
            canReview={updateIntegrationObject.canReview}
          />
        </div>
      </div>
    </Offcanvas>
  )
}
